(() => {
  'use strict';

  // A page may contain more than one FAQ section, each loading this asset.
  if (window.DifineyFaqTabs) {
    window.DifineyFaqTabs.init(document);
    return;
  }

  const instances = new WeakMap();
  const selector = '.faq-section[data-faq-tabs]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FaqDetails {
    constructor(details, id) {
      this.details = details;
      this.summary = details.querySelector('.faq-section__trigger');
      this.answer = details.querySelector('.faq-section__answer');
      this.animation = null;
      this.targetOpen = details.open;
      if (!this.summary || !this.answer) return;
      this.answer.id = this.answer.id || `${id}-answer`;
      this.summary.setAttribute('aria-controls', this.answer.id);
      this.summary.setAttribute('aria-expanded', String(details.open));

      this.onClick = (event) => {
        // Preserve links should a merchant include one in a question later.
        if (event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        this.setOpen(this.animation ? !this.targetOpen : !details.open);
      };
      this.onToggle = () => {
        if (!this.animation) {
          this.targetOpen = details.open;
          this.summary.setAttribute('aria-expanded', String(details.open));
        }
      };
      this.summary.addEventListener('click', this.onClick);
      details.addEventListener('toggle', this.onToggle);
    }

    setOpen(open, animate = true) {
      const startHeight = this.details.getBoundingClientRect().height;
      if (this.animation) {
        this.animation.cancel();
        this.animation = null;
      }
      this.targetOpen = open;
      this.summary.setAttribute('aria-expanded', String(open));

      if (!animate || reducedMotion.matches || !this.details.animate) {
        this.finish(open);
        return;
      }

      // Measure the same, original answer; no duplicate FAQ content is created.
      this.details.open = true;
      this.details.style.height = 'auto';
      const style = window.getComputedStyle(this.details);
      const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const endHeight = open
        ? this.details.getBoundingClientRect().height
        : this.summary.getBoundingClientRect().height + borders;
      this.details.style.height = `${startHeight}px`;
      this.details.style.overflow = 'hidden';
      const animation = this.details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 220, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
      );
      this.animation = animation;
      animation.onfinish = () => {
        if (this.animation === animation) this.finish(open);
      };
    }

    finish(open) {
      this.animation = null;
      this.details.open = open;
      this.details.style.height = '';
      this.details.style.overflow = '';
      this.targetOpen = open;
      this.summary.setAttribute('aria-expanded', String(open));
    }

    destroy() {
      if (this.animation) this.animation.cancel();
      this.finish(this.targetOpen);
      this.summary.removeEventListener('click', this.onClick);
      this.details.removeEventListener('toggle', this.onToggle);
    }
  }

  class FaqTabs {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector('.faq-section__content');
      if (!this.content) return;
      const allGroups = Array.from(this.content.children).filter((element) =>
        element.classList.contains('faq-section__group')
      );
      this.groups = allGroups.filter((element) =>
        element.querySelector('.faq-section__item') &&
        element.querySelector('.faq-section__group-title')
      );
      if (!this.groups.length) return;
      this.emptyGroups = allGroups.filter((group) => !this.groups.includes(group));
      this.emptyGroups.forEach((group) => { group.hidden = true; });
      this.details = [];
      this.tabs = [];
      this.tablist = document.createElement('div');
      this.tablist.className = 'faq-section__tabs';
      this.tablist.setAttribute('role', 'tablist');
      const heading = root.querySelector('.faq-section__heading');
      if (heading) {
        heading.id = heading.id || `${root.id}-heading`;
        this.tablist.setAttribute('aria-labelledby', heading.id);
      } else {
        this.tablist.setAttribute('aria-label', 'FAQ categories');
      }

      this.groups.forEach((group, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'faq-section__tab';
        tab.id = `${root.id}-tab-${index + 1}`;
        tab.textContent = group.querySelector('.faq-section__group-title').textContent;
        group.id = group.id || `${root.id}-panel-${index + 1}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', group.id);
        group.setAttribute('role', 'tabpanel');
        group.setAttribute('aria-labelledby', tab.id);
        group.tabIndex = 0;
        group.classList.remove('faq-section__group--hidden');
        tab.addEventListener('click', () => this.activate(index));
        this.tabs.push(tab);
        this.tablist.append(tab);

        group.querySelectorAll('.faq-section__item').forEach((details, itemIndex) => {
          if (details.querySelector('.faq-section__trigger') && details.querySelector('.faq-section__answer')) {
            this.details.push(new FaqDetails(details, `${group.id}-item-${itemIndex + 1}`));
          }
        });
      });

      this.onKeydown = (event) => {
        const current = this.tabs.indexOf(event.target);
        if (current < 0) return;
        const isRtl = window.getComputedStyle(this.tablist).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = (current + (isRtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (current + (isRtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.activate(next);
        this.tabs[next].focus({ preventScroll: true });
        const tab = this.tabs[next];
        const left = tab.offsetLeft - this.tablist.offsetLeft;
        if (left < this.tablist.scrollLeft || left + tab.offsetWidth > this.tablist.scrollLeft + this.tablist.clientWidth) {
          this.tablist.scrollTo({ left: left - 4, behavior: 'auto' });
        }
      };
      this.tablist.addEventListener('keydown', this.onKeydown);
      this.content.prepend(this.tablist);
      root.classList.add('faq-section--tabs-ready');
      this.activate(0);
    }

    activate(index) {
      this.groups.forEach((group, groupIndex) => {
        const selected = groupIndex === index;
        group.hidden = !selected;
        this.tabs[groupIndex].setAttribute('aria-selected', String(selected));
        this.tabs[groupIndex].tabIndex = selected ? 0 : -1;
      });
    }

    selectBlock(block) {
      const index = this.groups.findIndex((group) => group.contains(block));
      if (index < 0) return;
      this.activate(index);
      const accordion = this.details.find((item) => item.details === block || item.details.contains(block));
      if (accordion) accordion.setOpen(true, false);
    }

    destroy() {
      this.details.forEach((item) => item.destroy());
      this.tablist.removeEventListener('keydown', this.onKeydown);
      this.tablist.remove();
      this.groups.forEach((group) => { group.hidden = false; });
      this.emptyGroups.forEach((group) => { group.hidden = false; });
      this.root.classList.remove('faq-section--tabs-ready');
    }
  }

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    rootsWithin(scope).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new FaqTabs(root));
    });
  }

  window.DifineyFaqTabs = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => {
      const instance = instances.get(root);
      if (instance && instance.tablist) instance.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest(selector);
    const instance = root && instances.get(root);
    if (instance && instance.tablist) instance.selectBlock(event.target);
  });
})();
